import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconCheck,
  IconUpload,
  IconShield,
} from '@tabler/icons-react';

const QUAL_TYPES = [
  { value: 'forklift',    labelKey: 'qual.forklift'    },
  { value: 'sprayer',     labelKey: 'qual.sprayer'     },
  { value: 'cscs',        labelKey: 'qual.cscs'        },
  { value: 'driving',     labelKey: 'qual.driving'     },
  { value: 'firstaid',    labelKey: 'qual.firstaid'    },
  { value: 'chainsaw',    labelKey: 'qual.chainsaw'    },
  { value: 'pesticide',   labelKey: 'qual.pesticide'   },
  { value: 'telehandler', labelKey: 'qual.telehandler' },
  { value: 'atv',         labelKey: 'qual.atv'         },
  { value: 'other',       labelKey: 'qual.other'       },
];

export default function QualificationNew() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { orgId, userProfile } = useAuth();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const [type, setType] = useState('');
  const [customType, setCustomType] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [issuingBody, setIssuingBody] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [documentURL, setDocumentURL] = useState('');
  const [documentFile, setDocumentFile] = useState(null);
  const [documentName, setDocumentName] = useState('');

  const getStatus = () => {
    if (noExpiry || !expiryDate) return 'valid';
    const expiry = new Date(expiryDate);
    const today = new Date();
    const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'expired';
    if (days <= 30) return 'expiring';
    return 'valid';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentFile(file);
    setDocumentName(file.name);
  };

  const handleSave = async () => {
    if (!type) { setError(t('qual.err_type')); return; }
    if (type === 'other' && !customType.trim()) { setError(t('qual.err_name')); return; }
    if (!issueDate) { setError(t('qual.err_issue_date')); return; }
    if (!noExpiry && !expiryDate) { setError(t('qual.err_expiry')); return; }

    setSaving(true);
    setError('');

    try {
      let uploadedURL = documentURL;

      // Upload document if selected
      if (documentFile) {
        setUploading(true);
        const storageRef = ref(
          storage,
          `organisations/${orgId}/qualifications/${userId}/${Date.now()}_${documentFile.name}`
        );
        await uploadBytes(storageRef, documentFile);
        uploadedURL = await getDownloadURL(storageRef);
        setUploading(false);
      }

      const status = getStatus();

      await addDoc(
        collection(db, 'organisations', orgId, 'users', userId, 'qualifications'),
        {
          type,
          customType: type === 'other' ? customType.trim() : null,
          certificateNumber: certificateNumber.trim() || null,
          issuingBody: issuingBody.trim() || null,
          issueDate: issueDate || null,
          expiryDate: noExpiry ? null : expiryDate,
          noExpiry,
          documentURL: uploadedURL || null,
          documentName: documentName || null,
          status,
          addedBy: userProfile?.name || null,
          addedById: userProfile?.id || null,
          createdAt: serverTimestamp(),
        }
      );

      toast.success(t('qual.added_toast'));
      navigate(`/staff/${userId}`);
    } catch (err) {
      console.error(err);
      setError(t('qual.err_save'));
    }
    setSaving(false);
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 560, margin: '0 auto' }}>

        <div
          onClick={() => navigate(`/staff/${userId}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('qual.back_staff_profile')}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{t('staff.add_qualification')}</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          {t('qual.subtitle')}
        </p>

        {error && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Type selection */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('qual.type_title')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {QUAL_TYPES.map(qt => (
              <div
                key={qt.value}
                onClick={() => setType(qt.value)}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '0.5px solid',
                  borderColor: type === qt.value ? 'var(--color-brand)' : 'var(--color-border-light)',
                  background: type === qt.value ? 'var(--color-brand-light)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 12, color: type === qt.value ? 'var(--color-brand)' : 'var(--color-text-primary)', lineHeight: 1.3 }}>
                  {t(qt.labelKey)}
                </span>
                {type === qt.value && <IconCheck size={13} color="var(--color-brand)" stroke={2} />}
              </div>
            ))}
          </div>

          {type === 'other' && (
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('qual.name_label')} <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="text"
                placeholder={t('qual.name_placeholder')}
                value={customType}
                onChange={e => setCustomType(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
          )}
        </Card>

        {/* Certificate details */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('qual.cert_details')}</div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('qual.cert_number')} <span style={{ color: 'var(--color-text-tertiary)' }}>{t('common.optional_suffix')}</span>
            </label>
            <input
              type="text"
              placeholder={t('qual.cert_number_placeholder')}
              value={certificateNumber}
              onChange={e => setCertificateNumber(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'var(--font-mono)' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
              {t('qual.issuing_body')} <span style={{ color: 'var(--color-text-tertiary)' }}>{t('common.optional_suffix')}</span>
            </label>
            <input
              type="text"
              placeholder={t('qual.issuing_body_placeholder')}
              value={issuingBody}
              onChange={e => setIssuingBody(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('qual.issue_date')} <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                type="date"
                value={issueDate}
                onChange={e => setIssueDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {t('qual.expiry_date')} {!noExpiry && <span style={{ color: 'var(--color-danger)' }}>*</span>}
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={e => setExpiryDate(e.target.value)}
                disabled={noExpiry}
                style={{ width: '100%', boxSizing: 'border-box', opacity: noExpiry ? 0.4 : 1 }}
              />
            </div>
          </div>

          <div
            onClick={() => { setNoExpiry(!noExpiry); if (!noExpiry) setExpiryDate(''); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: 4,
              border: `1.5px solid ${noExpiry ? 'var(--color-brand)' : 'var(--color-border-medium)'}`,
              background: noExpiry ? 'var(--color-brand)' : '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {noExpiry && <IconCheck size={11} color="#fff" stroke={2.5} />}
            </div>
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              {t('qual.no_expiry_label')}
            </span>
          </div>
        </Card>

        {/* Document upload */}
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('qual.cert_document')}</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 12 }}>
            {t('qual.upload_note')}
          </div>

          {documentName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--color-success-light)', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-success)' }}>
              <IconCheck size={15} color="var(--color-success)" stroke={2} />
              <span style={{ fontSize: 13, color: 'var(--color-success)', flex: 1 }}>{documentName}</span>
              <button
                onClick={() => { setDocumentFile(null); setDocumentName(''); setDocumentURL(''); }}
                style={{ fontSize: 12, color: 'var(--color-text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {t('common.remove')}
              </button>
            </div>
          ) : (
            <label
              htmlFor="qual-doc-upload"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: 100, border: '0.5px dashed var(--color-border-medium)', borderRadius: 'var(--radius-lg)',
                cursor: 'pointer', gap: 6, color: 'var(--color-text-tertiary)', fontSize: 13,
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-medium)'}
            >
              <IconUpload size={20} stroke={1.5} />
              <span>{t('qual.click_upload')}</span>
              <span style={{ fontSize: 11 }}>{t('qual.file_types')}</span>
              <input
                id="qual-doc-upload"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </label>
          )}
        </Card>

        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate(`/staff/${userId}`)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            fullWidth
            onClick={handleSave}
            disabled={saving || uploading}
            loading={saving || uploading}
            icon={<IconShield size={14} stroke={1.5} />}
          >
            {uploading ? t('machines.uploading') : saving ? t('common.saving') : t('qual.save')}
          </Button>
        </div>

      </div>
    </Layout>
  );
}